/**
 * THE ONE RULE for the «Sin precio» badge on a transaction leg.
 *
 * ⚠️⚠️ WHY IT LIVES IN `shared`. The rule used to be written in the frontend
 * (`status-indicator-intent.ts`) and TRANSCRIBED twice in the backend: the API's
 * `problems` / `isReadyForTax` (`platform/transformers/transaction-readiness.ts`)
 * and the agreement test that pins the tax-report pre-flight to the badge
 * (`consistency-validation-checks.badge-agreement.integration.test.ts`). The
 * transcription drifted once already — for three weeks the green test compared
 * the SQL against a badge order that no longer existed (#2152). Importing the
 * rule instead of copying it is what makes "the badge, the API and the
 * pre-flight flag the same legs" a property of the code rather than of care.
 *
 * The SQL twin (`validation/missing-price-check.ts`) cannot import a function, so
 * it imports the one input this rule adds over the raw columns —
 * `FIAT_VALUE_SYMBOL_LIST` — and the agreement test checks the rest.
 */

import { fiatValueSymbolOf } from '../constants/fiat-value-symbols.js'

/** What a leg's status badge shows. */
export type LegPriceBadge =
  /** Raw ticker present but no catalog asset behind it → needs asset resolution. */
  | 'unresolved-asset'
  /** Asset resolved, but no fiat value can be obtained → needs a fiat value. */
  | 'missing-price'
  /** Nothing to flag — no badge is rendered. */
  | 'none'

/** The three legs a status badge can sit on. */
export type LegPriceBadgeLegKind = 'incoming' | 'outgoing' | 'commission'

/**
 * Everything the rule reads about one leg. `priceConfidence` is a plain string so
 * every caller's narrower union (the frontend's four values, the backend's eight)
 * fits without a cast.
 */
export interface LegPriceBadgeInput {
  assetId: string | null
  assetRaw: string | null
  priceConfidence: string | null
  /** Manual override. If present the leg is priced by definition. */
  userValue?: string | null
  /**
   * Value resolved by the list enricher, in the report currency. Populated ONLY
   * when there is no `userValue` AND a value could be resolved; null when the leg
   * has an override, is unresolved, or is UNPRICED.
   */
  marketValue?: string | null
  /**
   * Quantity on this leg. With no amount there is nothing to value, and the edit
   * modal disables its value editor accordingly. `undefined` = caller does not
   * know, assume present.
   */
  amount?: string | null
  /**
   * Which leg this is. The list enricher values the incoming and outgoing legs
   * ONLY, so a commission leg's null `marketValue` is STRUCTURAL, not evidence.
   * `undefined` keeps the pre-`legKind` behaviour.
   */
  legKind?: LegPriceBadgeLegKind
  /**
   * The OTHER side of the row (outgoing for an incoming leg, and vice versa) —
   * what `FiatValueService` priority 3 values this leg from. Never set for a
   * commission leg: the service has no counterparty step for it.
   * `undefined` = caller does not know, so priority 3 is not credited.
   */
  counterpartyAssetRaw?: string | null
  counterpartyAmount?: string | null
}

function isPresent(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== ''
}

/**
 * TRUE when `FiatValueService` values this leg WITHOUT consulting a market quote
 * — its priorities 1 to 3:
 *
 *   1. a manual override (`userValue`);
 *   2. the leg's own RAW ticker is fiat → its amount IS the value;
 *   3. the counterparty's RAW ticker is fiat and carries an amount → that amount
 *      is this leg's value («7 NEAR bought for 270 EUR» is worth 270 EUR).
 *
 * Only the RAW tickers count, exactly as in the service: a Kraken `ZEUR` resolves
 * to the EUR catalog asset but is priced from the catalog, and as a COUNTERPARTY
 * it values nothing (`XXBT → ZEUR` really is unpriceable without a candle).
 *
 * NOT modelled, on purpose: the FX conversion steps 2–3 still need when the fiat
 * currency differs from the report currency. The SQL twin cannot model it either
 * (it is not given the report currency), and a missing ECB rate is a systemic
 * condition with no per-row remedy to send the user to.
 */
export function legIsValuedWithoutAMarketQuote(leg: LegPriceBadgeInput): boolean {
  if (isPresent(leg.userValue)) return true
  if (fiatValueSymbolOf(leg.assetRaw) !== null) return true
  if (leg.legKind === 'commission') return false
  return fiatValueSymbolOf(leg.counterpartyAssetRaw) !== null && isPresent(leg.counterpartyAmount)
}

/**
 * Which badge a leg shows. The order of the questions IS the rule:
 *
 *  1. Unresolved wins: without a catalog asset there is nothing to price yet.
 *  2. No quantity ⇒ nothing to price. Badging it would send the user to a value
 *     editor that is DISABLED for exactly this case.
 *  3. A manual value satisfies what the chip asks for, whatever the enum says
 *     (measured 2026-08-14: a value saved with «Editar valor» on a stamped leg
 *     left the chip asking for the value the user had just given).
 *  4. An EXPLICIT stamp `priceConfidence = 'missing'` is the engine reporting "I
 *     looked and found no market price" — real evidence on any leg, commission
 *     included — UNLESS the leg is valued without a market quote (own fiat
 *     ticker, fiat counterparty). ⚠️⚠️ The stamp is TRUE about the MARKET quote,
 *     but the chip does not report that: it ASKS for a value, and the report
 *     already has one. Measured in e2e (test1, ES 2025): `trade` +7 NEAR (stamped
 *     `missing`) / −270 EUR wore «Sin precio» and, under a jurisdiction that
 *     blocks on pre-report errors, kept `POST /api/tax-reports/generate` at 422 —
 *     while the same report valued the purchase at 270 EUR through the fiat
 *     counterparty.
 *  5. Below here the only signal is an ABSENT value, meaningful only on a leg the
 *     enricher visits — never the commission.
 *  6. The enrichment pipeline stamps; a leg it never visited carries NULL, not
 *     'missing'. No stamp AND no resolvable market value IS unpriced. Here
 *     `marketValue` already embodies the whole chain (fiat amounts included,
 *     and the FX conversion the symbol list cannot see), so it alone decides.
 */
export function resolveLegPriceBadge(leg: LegPriceBadgeInput): LegPriceBadge {
  if (leg.assetRaw !== null && leg.assetId === null) return 'unresolved-asset'
  if (leg.assetId === null) return 'none'

  const hasQuantity =
    leg.amount === undefined ||
    (leg.amount !== null && leg.amount !== '' && parseFloat(leg.amount) !== 0)
  if (!hasQuantity) return 'none'

  if (isPresent(leg.userValue)) return 'none'

  if (leg.priceConfidence === 'missing') {
    return legIsValuedWithoutAMarketQuote(leg) ? 'none' : 'missing-price'
  }

  if (leg.legKind === 'commission') return 'none'

  if (leg.priceConfidence === null && !isPresent(leg.marketValue)) return 'missing-price'

  return 'none'
}
